import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { FaPlus, FaTimes } from 'react-icons/fa';
import {
  addNoteReceivable,
  getCustomers,
  clearError,
  clearSuccess,
  addCustomer,
  getProjects,
  addProject,
  getCostCenters,
  addCostCenter,
  getTaxes,
  addTax
} from '../../../../redux/Slices/authSlice';
import i18n from '../../../../i18n/i18n';

// add note receivable component
const AddNoteReceivable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const {
    customers,
    customersPagination,
    projects,
    projectsPagination,
    costCenters,
    costCentersPagination,
    taxes,
    taxesPagination,
    isLoading,
    error,
    success
  } = useSelector((s) => s.auth);

  const [form, setForm] = useState({
    customer_id: '',
    project_id: '',
    amount: '',
    issue_date: '',
    due_date: '',
    note_number: '',
    description: '',
  });

  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerCurrentPage, setCustomerCurrentPage] = useState(1);
  const customerSearchInputRef = useRef(null);
  const customerSearchTimeoutRef = useRef(null);

  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [projectCurrentPage, setProjectCurrentPage] = useState(1);
  const [projectSearchInputRef, projectSearchTimeoutRef] = [useRef(null), useRef(null)];
  const [projModalCustomerDropdownOpen, setProjModalCustomerDropdownOpen] = useState(false);
  const [projModalCustomerSearchTerm, setProjModalCustomerSearchTerm] = useState('');
  const [projModalCustomerCurrentPage, setProjModalCustomerCurrentPage] = useState(1);
  const projModalCustomerSearchInputRef = useRef(null);
  const projModalCustomerSearchTimeoutRef = useRef(null);

  const [taxDropdownOpen, setTaxDropdownOpen] = useState(false);
  const [taxSearchTerm, setTaxSearchTerm] = useState('');
  const [taxCurrentPage, setTaxCurrentPage] = useState(1);
  const taxSearchInputRef = useRef(null);
  const taxSearchTimeoutRef = useRef(null);

  const [costCenterDropdownOpen, setCostCenterDropdownOpen] = useState(false);
  const [costCenterSearchTerm, setCostCenterSearchTerm] = useState('');
  const [costCenterCurrentPage, setCostCenterCurrentPage] = useState(1);
  const costCenterSearchInputRef = useRef(null);
  const costCenterSearchTimeoutRef = useRef(null);

  const lastErrorRef = useRef({ message: null, time: 0 });

  // Modals state
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [showAddTaxModal, setShowAddTaxModal] = useState(false);
  const [showAddCostCenterModal, setShowAddCostCenterModal] = useState(false);

  const [customerForm, setCustomerForm] = useState({
    name_en: '',
    name_ar: '',
    code: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    tax_number: '',
    is_active: true,
    notes: ''
  });

  const [projectForm, setProjectForm] = useState({
    code: '',
    name_ar: '',
    name_en: '',
    customer_id: null,
    start_date: '',
    end_date: '',
    status: 'planned',
    contract_value: '',
    tax_id: null,
    cost_center_id: null,
    notes: '',
  });

  const [taxForm, setTaxForm] = useState({
    name_ar: '',
    name_en: '',
    rate: '',
    is_active: true,
  });

  const [costCenterForm, setCostCenterForm] = useState({
    name_ar: '',
    name_en: '',
    code: '',
  });

  const [isSubmittingCustomer, setIsSubmittingCustomer] = useState(false);
  const [isSubmittingProject, setIsSubmittingProject] = useState(false);

  // Focus management
  const projectNotesTextareaRef = useRef(null);

  useEffect(() => {
    dispatch(getCustomers({ page: customerCurrentPage, per_page: 10, search: customerSearchTerm || undefined }));
  }, [dispatch, customerCurrentPage]);

  useEffect(() => {
    if (customerSearchTerm !== undefined) {
      if (customerSearchTimeoutRef.current) clearTimeout(customerSearchTimeoutRef.current);
      customerSearchTimeoutRef.current = setTimeout(() => {
        setCustomerCurrentPage(1);
        dispatch(getCustomers({ page: 1, per_page: 10, search: customerSearchTerm || undefined }));
      }, 300);
    }
  }, [customerSearchTerm, dispatch]);

  // Projects fetching
  useEffect(() => {
    dispatch(getProjects({ page: projectCurrentPage, per_page: 10, search: projectSearchTerm || undefined }));
  }, [dispatch, projectCurrentPage]);

  useEffect(() => {
    if (projectSearchTerm !== undefined) {
      if (projectSearchTimeoutRef.current) clearTimeout(projectSearchTimeoutRef.current);
      projectSearchTimeoutRef.current = setTimeout(() => {
        setProjectCurrentPage(1);
        dispatch(getProjects({ page: 1, per_page: 10, search: projectSearchTerm || undefined }));
      }, 300);
    }
  }, [projectSearchTerm, dispatch]);

  // Taxes fetching
  useEffect(() => {
    if (taxDropdownOpen) {
      dispatch(getTaxes({ page: taxCurrentPage, per_page: 10, search: taxSearchTerm || undefined }));
    }
  }, [dispatch, taxCurrentPage, taxDropdownOpen]);

  useEffect(() => {
    if (taxSearchTerm !== undefined && taxDropdownOpen) {
      if (taxSearchTimeoutRef.current) clearTimeout(taxSearchTimeoutRef.current);
      taxSearchTimeoutRef.current = setTimeout(() => {
        setTaxCurrentPage(1);
        dispatch(getTaxes({ page: 1, per_page: 10, search: taxSearchTerm || undefined }));
      }, 300);
    }
  }, [taxSearchTerm, taxDropdownOpen, dispatch]);

  // Cost Centers fetching
  useEffect(() => {
    if (costCenterDropdownOpen) {
      dispatch(getCostCenters({ page: costCenterCurrentPage, per_page: 10, search: costCenterSearchTerm || undefined }));
    }
  }, [dispatch, costCenterCurrentPage, costCenterDropdownOpen]);

  useEffect(() => {
    if (costCenterSearchTerm !== undefined && costCenterDropdownOpen) {
      if (costCenterSearchTimeoutRef.current) clearTimeout(costCenterSearchTimeoutRef.current);
      costCenterSearchTimeoutRef.current = setTimeout(() => {
        setCostCenterCurrentPage(1);
        dispatch(getCostCenters({ page: 1, per_page: 10, search: costCenterSearchTerm || undefined }));
      }, 300);
    }
  }, [costCenterSearchTerm, costCenterDropdownOpen, dispatch]);

  useEffect(() => {
    if (success && !showAddCustomerModal && !showAddProjectModal && !showAddTaxModal && !showAddCostCenterModal) {
      toast.success(success, { rtl: true });
      dispatch(clearSuccess());
      navigate('/notes-receivable');
    }
  }, [success, dispatch, navigate, showAddCustomerModal, showAddProjectModal, showAddTaxModal, showAddCostCenterModal]);

  useEffect(() => {
    if (error) {
      // Don't show global error if any modal is open, to avoid double toasts
      // Modals now handle their own error toasts locally
      if (showAddCustomerModal || showAddProjectModal || showAddTaxModal || showAddCostCenterModal) {
        return;
      }

      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: true });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 2500);
    }
  }, [error, dispatch, showAddCustomerModal, showAddProjectModal, showAddTaxModal, showAddCostCenterModal]);

  const displayCustomers = Array.isArray(customers) ? customers : [];
  const customerPagination = customersPagination || { current_page: 1, last_page: 1 };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.customer_id) {
      toast.error(t('notesReceivable.form.requiredFields', { defaultValue: 'برجاء استكمال الحقول المطلوبة' }), { rtl: isRTL });
      return;
    }
    if (!form.amount || form.amount === '' || form.amount === '0') {
      toast.error(t('notesReceivable.form.amountRequired', { defaultValue: 'القيمة مطلوبة' }), { rtl: isRTL });
      return;
    }
    const payload = {
      customer_id: Number(form.customer_id),
      amount: Number(form.amount),
    };

    if (form.project_id) {
      payload.project_id = Number(form.project_id);
    }
    // Only include optional fields if they have values
    if (form.issue_date) {
      payload.issue_date = form.issue_date;
    }
    if (form.due_date) {
      payload.due_date = form.due_date;
    }
    if (form.note_number) {
      payload.note_number = form.note_number;
    }
    if (form.description) {
      payload.description = form.description;
    }

    dispatch(addNoteReceivable(payload));
  };

  const renderCustomerDisplay = (customerId) => {
    if (!customerId) return t('notesReceivable.form.selectCustomer', { defaultValue: 'اختر العميل' });
    const selected = (customers || []).find((c) => String(c.id) === String(customerId));
    if (!selected) return t('notesReceivable.form.selectCustomer', { defaultValue: 'اختر العميل' });
    return `${selected.code ? `${selected.code} - ` : ''}${isRTL ? (selected.name_ar || selected.name || '') : (selected.name_en || selected.name_ar || selected.name || '')}`;
  };

  const handleCustomerFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCustomerForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleProjectFormChange = (e) => {
    const { name, value } = e.target;
    const isTextarea = e.target.tagName === 'TEXTAREA';
    const cursorPosition = isTextarea ? e.target.selectionStart : null;

    setProjectForm(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'notes' && projectNotesTextareaRef.current && isTextarea) {
      setTimeout(() => {
        const textarea = projectNotesTextareaRef.current;
        if (textarea) {
          textarea.focus();
          if (cursorPosition !== null) {
            textarea.setSelectionRange(cursorPosition, cursorPosition);
          }
        }
      }, 0);
    }
  };

  const handleTaxFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTaxForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCostCenterFormChange = (e) => {
    const { name, value } = e.target;
    setCostCenterForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    if (!customerForm.name_ar || !customerForm.name_en) {
      toast.error(t('quotations.add.modals.addCustomer.requiredFields', { defaultValue: 'برجاء إدخال الاسم بالعربية والإنجليزية' }), { rtl: isRTL });
      return;
    }
    try {
      setIsSubmittingCustomer(true);
      const submitData = {
        ...customerForm,
        is_active: customerForm.is_active ? true : false
      };
      const result = await dispatch(addCustomer(submitData)).unwrap();
      if (result?.data?.id) {
        dispatch(clearSuccess());
        if (showAddProjectModal) {
          setProjectForm(prev => ({ ...prev, customer_id: result.data.id }));
        } else {
          setForm(prev => ({ ...prev, customer_id: result.data.id }));
        }
        await dispatch(getCustomers({ page: 1, per_page: 10 }));
        setShowAddCustomerModal(false);
        setCustomerForm({
          name_en: '',
          name_ar: '',
          code: '',
          contact_person: '',
          email: '',
          phone: '',
          address: '',
          tax_number: '',
          is_active: true,
          notes: ''
        });
        toast.success(t('quotations.add.modals.addCustomer.success', { defaultValue: 'تم إضافة العميل بنجاح' }), { rtl: isRTL });
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || t('quotations.add.modals.addCustomer.error', { defaultValue: 'فشل إضافة العميل' }), { rtl: isRTL });
    } finally {
      setIsSubmittingCustomer(false);
    }
  };

  const handleAddProject = async (e) => {
    e.preventDefault();
    if (!projectForm.name_ar) {
      toast.error(t('quotations.add.modals.addProject.requiredFields', { defaultValue: 'برجاء إدخال الاسم بالعربية' }), { rtl: isRTL });
      return;
    }
    try {
      setIsSubmittingProject(true);
      const payload = {
        ...projectForm,
        contract_value: projectForm.contract_value ? Number(projectForm.contract_value) : undefined,
      };
      const result = await dispatch(addProject(payload)).unwrap();
      if (result?.data?.id) {
        dispatch(clearSuccess());
        setForm(prev => ({ ...prev, project_id: result.data.id }));
        await dispatch(getProjects({ page: 1, per_page: 10 }));
        setShowAddProjectModal(false);
        setProjectForm({
          code: '',
          name_ar: '',
          name_en: '',
          customer_id: null,
          start_date: '',
          end_date: '',
          status: 'planned',
          contract_value: '',
          tax_id: null,
          cost_center_id: null,
          notes: '',
        });
        toast.success(t('quotations.add.modals.addProject.success', { defaultValue: 'تم إضافة المشروع بنجاح' }), { rtl: isRTL });
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || t('quotations.add.modals.addProject.error', { defaultValue: 'فشل إضافة المشروع' }), { rtl: isRTL });
    } finally {
      setIsSubmittingProject(false);
    }
  };

  const handleAddTax = async (e) => {
    e.preventDefault();
    if (!taxForm.name_ar || !taxForm.name_en || !taxForm.rate) {
      toast.error(t('quotations.add.modals.addTax.requiredFields', { defaultValue: 'برجاء استكمال الحقول المطلوبة' }), { rtl: isRTL });
      return;
    }
    try {
      const payload = { ...taxForm, rate: Number(taxForm.rate) };
      const result = await dispatch(addTax(payload)).unwrap();
      if (result?.data?.id) {
        dispatch(clearSuccess());
        setProjectForm(prev => ({ ...prev, tax_id: result.data.id }));
        await dispatch(getTaxes({ page: 1, per_page: 10 }));
        setShowAddTaxModal(false);
        setTaxForm({ name_ar: '', name_en: '', rate: '', is_active: true });
        toast.success(t('quotations.add.modals.addTax.success', { defaultValue: 'تم إضافة الضريبة بنجاح' }), { rtl: isRTL });
      }
    } catch (error) {
      toast.error(error?.message || t('quotations.add.modals.addTax.error', { defaultValue: 'فشل إضافة الضريبة' }), { rtl: isRTL });
    }
  };

  const handleAddCostCenter = async (e) => {
    e.preventDefault();
    if (!costCenterForm.name_ar || !costCenterForm.name_en) {
      toast.error(t('quotations.add.modals.addCostCenter.requiredFields', { defaultValue: 'برجاء استكمال الحقول المطلوبة' }), { rtl: isRTL });
      return;
    }
    try {
      const result = await dispatch(addCostCenter(costCenterForm)).unwrap();
      if (result?.data?.id) {
        dispatch(clearSuccess());
        setProjectForm(prev => ({ ...prev, cost_center_id: result.data.id }));
        await dispatch(getCostCenters({ page: 1, per_page: 10 }));
        setShowAddCostCenterModal(false);
        setCostCenterForm({ name_ar: '', name_en: '', code: '' });
        toast.success(t('quotations.add.modals.addCostCenter.success', { defaultValue: 'تم إضافة مركز التكلفة بنجاح' }), { rtl: isRTL });
      }
    } catch (error) {
      toast.error(error?.message || t('quotations.add.modals.addCostCenter.error', { defaultValue: 'فشل إضافة مركز التكلفة' }), { rtl: isRTL });
    }
  };

  const renderDropdownItemLabel = (item, codeField, nameArField, nameEnField) => {
    const code = item[codeField] || '';
    const name = isRTL
      ? (item[nameArField] || item[nameEnField] || '')
      : (item[nameEnField] || item[nameArField] || '');
    return `${code ? `${code} - ` : ''}${name}`;
  };

  const renderProjectDisplay = (projectId) => {
    if (!projectId) return t('notesReceivable.form.selectProject', { defaultValue: 'اختر المشروع' });
    const selected = (projects || []).find((p) => String(p.id) === String(projectId));
    return selected ? renderDropdownItemLabel(selected, 'code', 'name_ar', 'name_en') : t('notesReceivable.form.selectProject', { defaultValue: 'اختر المشروع' });
  };

  useEffect(() => {
    if (customerDropdownOpen && customerSearchInputRef.current) {
      customerSearchInputRef.current.focus();
    }
    if (projectDropdownOpen && projectSearchInputRef.current) {
      projectSearchInputRef.current.focus();
    }
  }, [customerDropdownOpen, projectDropdownOpen]);

  const DropdownWithSearch = ({ isOpen, onToggle, searchTerm, onSearchChange, searchInputRef, items, pagination, currentPage, onPageChange, onSelect, displayValue, placeholder, label, renderItem }) => {
    const internalInputRef = useRef(null);
    const inputRef = searchInputRef || internalInputRef;

    useEffect(() => {
      if (isOpen && inputRef?.current) {
        const timer = setTimeout(() => {
          inputRef.current?.focus({ preventScroll: true });
        }, 0);
        return () => clearTimeout(timer);
      }
    }, [isOpen, searchTerm, items, inputRef]);

    if (!isOpen) return null;

    return (
      <div style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0, background: '#1a1f2e', border: '1px solid #333', borderRadius: 8, marginTop: 4, maxHeight: 400, overflowY: 'auto' }}>
        <div style={{ padding: 8 }}>
          <input
            ref={inputRef}
            type="text"
            placeholder={`${t('quotations.add.search', { defaultValue: 'ابحث عن' })} ${label}`}
            value={searchTerm}
            onChange={(e) => {
              onSearchChange(e.target.value);
              setTimeout(() => {
                inputRef.current?.focus({ preventScroll: true });
              }, 0);
            }}
            onFocus={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            style={{ width: '100%', padding: 8, backgroundColor: '#14263b', border: '1px solid #444', borderRadius: 6, color: 'white', fontSize: 14 }}
          />
        </div>
        {pagination && pagination.last_page >= 1 && (
          <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ color: '#ccc', fontSize: 11, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('common.pagination.page', { defaultValue: 'الصفحة' })} {pagination.current_page} {t('common.pagination.of', { defaultValue: 'من' })} {pagination.last_page}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page > 1) onPageChange(1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>««</button>
              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page > 1) onPageChange(pagination.current_page - 1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>‹</button>
              {(() => {
                const pages = [];
                const current = pagination.current_page;
                const last = pagination.last_page;
                const showPages = 5;
                let start = Math.max(1, current - Math.floor(showPages / 2));
                let end = Math.min(last, start + showPages - 1);
                if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);
                for (let i = start; i <= end; i++) pages.push(i);
                return pages.map((page) => (
                  <button key={page} type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPageChange(page); }} style={{ padding: '4px 8px', backgroundColor: page === current ? '#AC2000' : '#1a1f2e', color: 'white', border: page === current ? '1px solid #AC2000' : '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10, fontWeight: page === current ? 'bold' : 'normal', minWidth: '28px' }}>{page}</button>
                ));
              })()}
              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.current_page + 1); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>›</button>
              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.last_page); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>»»</button>
            </div>
          </div>
        )}
        <div>
          {(Array.isArray(items) ? items : []).map((item) => (
            <div
              key={item.id}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onSelect(item.id);
              }}
              style={{
                padding: '12px 15px',
                cursor: 'pointer',
                borderTop: '1px solid #333',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                color: 'white',
                fontSize: '14px',
                transition: 'background-color 0.2s',
                direction: isRTL ? 'rtl' : 'ltr',
                textAlign: isRTL ? 'right' : 'left'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#14263b'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <span style={{ color: 'white' }}>
                {renderItem ? renderItem(item) : renderDropdownItemLabel(item, 'code', 'name_ar', 'name_en')}
              </span>
            </div>
          ))}
          {(!Array.isArray(items) || items.length === 0) && <div style={{ padding: 12, textAlign: 'center', color: '#bbb', direction: isRTL ? 'rtl' : 'ltr' }}>{t('quotations.add.noResults', { defaultValue: 'لا توجد نتائج' })}</div>}
        </div>
      </div>
    );
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      const isDropdownContainer = target.closest('[data-dropdown-container]');
      const isDropdownToggle = target.closest('[data-dropdown-toggle]');

      if (!isDropdownContainer && !isDropdownToggle) {
        if (customerDropdownOpen) setCustomerDropdownOpen(false);
        if (projectDropdownOpen) setProjectDropdownOpen(false);
        if (taxDropdownOpen) setTaxDropdownOpen(false);
        if (costCenterDropdownOpen) setCostCenterDropdownOpen(false);
        if (projModalCustomerDropdownOpen) setProjModalCustomerDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [customerDropdownOpen, projectDropdownOpen, taxDropdownOpen, costCenterDropdownOpen, projModalCustomerDropdownOpen]);

  return (
    <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white', direction: isRTL ? 'rtl' : 'ltr' }}>
      <h1 style={{ marginTop: 0, marginBottom: 16, fontSize: '20px', textAlign: isRTL ? 'right' : 'left' }}>{t('notesReceivable.form.title', { defaultValue: 'إضافة ورقة قبض' })}</h1>
      <form
        onSubmit={handleSubmit}
        style={{
          backgroundColor: '#1b3350',
          border: '1px solid #333',
          borderRadius: 12,
          padding: 24,
          display: 'grid',
          gap: 16,
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
          <div style={{ position: 'relative' }} data-dropdown-container>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <label style={{ display: 'block', fontSize: 14, flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
                {t('notesReceivable.form.customer', { defaultValue: 'العميل' })} <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAddCustomerModal(true);
                }}
                style={{
                  backgroundColor: '#AC2000',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  padding: '10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '40px',
                  height: '40px'
                }}
                title="إضافة عميل جديد"
              >
                <FaPlus />
              </button>
            </div>
            <div
              data-dropdown-toggle
              onClick={() => {
                if (customerDropdownOpen) {
                  setCustomerSearchTerm('');
                  setCustomerCurrentPage(1);
                }
                setCustomerDropdownOpen((p) => !p);
              }}
              style={{
                width: '100%',
                padding: 12,
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: 8,
                color: 'white',
                fontSize: 14,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                boxSizing: 'border-box',
              }}
            >
              <span>{renderCustomerDisplay(form.customer_id)}</span>
              <span style={{ fontSize: 12 }}>▼</span>
            </div>
            {customerDropdownOpen && (
              <DropdownWithSearch
                isOpen={customerDropdownOpen}
                onToggle={setCustomerDropdownOpen}
                searchTerm={customerSearchTerm}
                onSearchChange={setCustomerSearchTerm}
                searchInputRef={customerSearchInputRef}
                items={displayCustomers}
                pagination={customerPagination}
                currentPage={customerCurrentPage}
                onPageChange={setCustomerCurrentPage}
                onSelect={(id) => {
                  setForm((prev) => ({ ...prev, customer_id: id }));
                  setCustomerDropdownOpen(false);
                }}
                label={t('notesReceivable.form.customer', { defaultValue: 'العميل' })}
              />
            )}
          </div>

          <div style={{ position: 'relative' }} data-dropdown-container>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <label style={{ display: 'block', fontSize: 14, flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
                {t('notesReceivable333.form.project', { defaultValue: 'المشروع' })}
              </label>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAddProjectModal(true);
                }}
                style={{
                  backgroundColor: '#AC2000',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  padding: '10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '40px',
                  height: '40px'
                }}
                title="إضافة مشروع جديد"
              >
                <FaPlus />
              </button>
            </div>
            <div
              data-dropdown-toggle
              onClick={() => {
                if (projectDropdownOpen) {
                  setProjectSearchTerm('');
                  setProjectCurrentPage(1);
                }
                setProjectDropdownOpen((p) => !p);
              }}
              style={{
                width: '100%',
                padding: 12,
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: 8,
                color: 'white',
                fontSize: 14,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                boxSizing: 'border-box',
              }}
            >
              <span>{renderProjectDisplay(form.project_id)}</span>
              <span style={{ fontSize: 12 }}>▼</span>
            </div>
            {projectDropdownOpen && (
              <DropdownWithSearch
                isOpen={projectDropdownOpen}
                onToggle={setProjectDropdownOpen}
                searchTerm={projectSearchTerm}
                onSearchChange={setProjectSearchTerm}
                searchInputRef={projectSearchInputRef}
                items={Array.isArray(projects) ? projects : []}
                pagination={projectsPagination}
                currentPage={projectCurrentPage}
                onPageChange={setProjectCurrentPage}
                onSelect={(id) => {
                  setForm((prev) => ({ ...prev, project_id: id }));
                  setProjectDropdownOpen(false);
                }}
                label={t('notesReceivable333.form.project', { defaultValue: 'المشروع' })}
              />
            )}
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <label style={{ display: 'block', fontSize: 14, flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
                {t('notesReceivable.form.amount', { defaultValue: 'القيمة' })} <span style={{ color: '#AC2000' }}>*</span>
              </label>
              <div style={{ minWidth: '40px', height: '40px' }}></div>
            </div>
            <input
              type="number"

              name="amount"
              value={form.amount}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: 12,
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: 8,
                color: 'white',
                fontSize: 14,
                direction: isRTL ? 'rtl' : 'ltr',
                textAlign: isRTL ? 'right' : 'left',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, textAlign: isRTL ? 'right' : 'left' }}>
              {t('notesReceivable.form.issueDate', { defaultValue: 'تاريخ الإصدار' })}
            </label>
            <input
              type="date"
              name="issue_date"
              value={form.issue_date}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 12,
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: 8,
                color: 'white',
                fontSize: 14,
                direction: isRTL ? 'rtl' : 'ltr',
                textAlign: isRTL ? 'right' : 'left',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, textAlign: isRTL ? 'right' : 'left' }}>
              {t('notesReceivable.form.dueDate', { defaultValue: 'تاريخ الاستحقاق' })}
            </label>
            <input
              type="date"
              name="due_date"
              value={form.due_date}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 12,
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: 8,
                color: 'white',
                fontSize: 14,
                direction: isRTL ? 'rtl' : 'ltr',
                textAlign: isRTL ? 'right' : 'left',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, textAlign: isRTL ? 'right' : 'left' }}>
              {t('notesReceivable.form.noteNumber', { defaultValue: 'رقم الورقة' })}
            </label>
            <input
              type="text"
              name="note_number"
              value={form.note_number}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 12,
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: 8,
                color: 'white',
                fontSize: 14,
                direction: isRTL ? 'rtl' : 'ltr',
                textAlign: isRTL ? 'right' : 'left',
              }}
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, textAlign: isRTL ? 'right' : 'left' }}>{t('notesReceivable.form.description', { defaultValue: 'الوصف' })}</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={3}
              style={{
                width: '100%',
                padding: 12,
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: 8,
                color: 'white',
                fontSize: 14,
                resize: 'vertical',
              }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 24 }}>
          <button
            type="button"
            onClick={() => navigate('/notes-receivable')}
            style={{
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '10px 16px',
              cursor: 'pointer',
            }}
          >
            {t('notesReceivable.form.cancel', { defaultValue: 'إلغاء' })}
          </button>
          <button
            type="submit"
            disabled={isLoading}
            style={{
              backgroundColor: '#AC2000',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '10px 16px',
              cursor: 'pointer',
              direction: isRTL ? 'rtl' : 'ltr',
              textAlign: isRTL ? 'right' : 'left'
            }}
          >
            {t('notesReceivable.form.save', { defaultValue: 'حفظ' })}
          </button>
        </div>
      </form>

      {/* Add Customer Modal */}
      {showAddCustomerModal && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setShowAddCustomerModal(false)}
        >
          <div
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addCustomer.title', { defaultValue: 'إضافة عميل جديد' })}</h2>
              <button
                onClick={() => {
                  setShowAddCustomerModal(false);
                  setCustomerForm({
                    name_en: '',
                    name_ar: '',
                    code: '',
                    contact_person: '',
                    email: '',
                    phone: '',
                    address: '',
                    tax_number: '',
                    is_active: true,
                    notes: ''
                  });
                }}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddCustomer}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCustomer.nameEn', { defaultValue: 'الاسم بالإنجليزية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_en"
                    value={customerForm.name_en}
                    onChange={handleCustomerFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCustomer.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_ar"
                    value={customerForm.name_ar}
                    onChange={handleCustomerFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCustomer.code', { defaultValue: 'الكود' })}
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={customerForm.code}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCustomer.contactPerson', { defaultValue: 'الشخص المسؤول' })}
                  </label>
                  <input
                    type="text"
                    name="contact_person"
                    value={customerForm.contact_person}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCustomer.email', { defaultValue: 'البريد الإلكتروني' })}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={customerForm.email}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCustomer.phone', { defaultValue: 'الهاتف' })}
                  </label>
                  <input
                    type="text"
                    name="phone"
                    value={customerForm.phone}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCustomer.address', { defaultValue: 'العنوان' })}
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={customerForm.address}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCustomer.taxNumber', { defaultValue: 'الرقم الضريبي' })}
                  </label>
                  <input
                    type="text"
                    name="tax_number"
                    value={customerForm.tax_number}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={customerForm.is_active}
                    onChange={handleCustomerFormChange}
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                  <label style={{ color: 'white', fontSize: '14px', cursor: 'pointer' }}>
                    {t('quotations.add.modals.addCustomer.isActive', { defaultValue: 'نشط' })}
                  </label>
                </div>
              </div>
              <div style={{ marginTop: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                  {t('quotations.add.modals.addCustomer.notes', { defaultValue: 'ملاحظات' })}
                </label>
                <textarea
                  name="notes"
                  value={customerForm.notes}
                  onChange={handleCustomerFormChange}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    resize: 'vertical',
                    direction: isRTL ? 'rtl' : 'ltr',
                    textAlign: isRTL ? 'right' : 'left'
                  }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px' }}>
                <button
                  type="button"
                  onClick={() => setShowAddCustomerModal(false)}
                  style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {t('common.cancel', { defaultValue: 'إلغاء' })}
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingCustomer}
                  style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: isSubmittingCustomer ? 'not-allowed' : 'pointer', opacity: isSubmittingCustomer ? 0.7 : 1 }}
                >
                  {isSubmittingCustomer ? t('common.saving', { defaultValue: 'جاري الحفظ...' }) : t('common.save', { defaultValue: 'حفظ' })}
                </button>
              </div>
            </form>
          </div>
        </div>
        , document.body)}

      {/* Add Project Modal */}
      {showAddProjectModal && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setShowAddProjectModal(false)}
        >
          <div
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.title', { defaultValue: 'إضافة مشروع جديد' })}</h2>
              <button
                onClick={() => setShowAddProjectModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddProject}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addProject.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_ar"
                    value={projectForm.name_ar}
                    onChange={handleProjectFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addProject.nameEn', { defaultValue: 'الاسم بالإنجليزية' })}
                  </label>
                  <input
                    type="text"
                    name="name_en"
                    value={projectForm.name_en}
                    onChange={handleProjectFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addProject.code', { defaultValue: 'كود المشروع' })}
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={projectForm.code}
                    onChange={handleProjectFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>

                <div style={{ position: 'relative' }} data-dropdown-container>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 'bold', color: 'white', flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addProject.customer', { defaultValue: 'العميل' })}
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowAddCustomerModal(true)}
                      style={{
                        backgroundColor: '#AC2000',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '44px',
                        height: '44px'
                      }}
                      title="إضافة عميل جديد"
                    >
                      <FaPlus />
                    </button>
                  </div>
                  <div
                    data-dropdown-toggle
                    onClick={() => {
                      setTaxDropdownOpen(false);
                      setCostCenterDropdownOpen(false);
                      setProjModalCustomerDropdownOpen((p) => !p);
                    }}
                    style={{ flex: 1, padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box' }}
                  >
                    <span style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {projectForm.customer_id ? (customers || []).find(c => String(c.id) === String(projectForm.customer_id)) ? renderDropdownItemLabel((customers || []).find(c => String(c.id) === String(projectForm.customer_id)), 'code', 'name_ar', 'name_en') : t('quotations.add.modals.addProject.selectCustomer', { defaultValue: 'اختر العميل' }) : t('quotations.add.modals.addProject.selectCustomer', { defaultValue: 'اختر العميل' })}
                    </span>
                    <span style={{ fontSize: 12 }}>▼</span>
                  </div>
                  {projModalCustomerDropdownOpen && (
                    <DropdownWithSearch
                      isOpen={projModalCustomerDropdownOpen}
                      onToggle={setProjModalCustomerDropdownOpen}
                      searchTerm={projModalCustomerSearchTerm}
                      onSearchChange={(val) => {
                        setProjModalCustomerSearchTerm(val);
                        if (projModalCustomerSearchTimeoutRef.current) clearTimeout(projModalCustomerSearchTimeoutRef.current);
                        projModalCustomerSearchTimeoutRef.current = setTimeout(() => {
                          setProjModalCustomerCurrentPage(1);
                          dispatch(getCustomers({ page: 1, per_page: 10, search: val || undefined }));
                        }, 300);
                      }}
                      searchInputRef={projModalCustomerSearchInputRef}
                      items={Array.isArray(customers) ? customers : []}
                      pagination={customersPagination}
                      currentPage={projModalCustomerCurrentPage}
                      onPageChange={(page) => {
                        setProjModalCustomerCurrentPage(page);
                        dispatch(getCustomers({ page, per_page: 10, search: projModalCustomerSearchTerm || undefined }));
                      }}
                      onSelect={(id) => {
                        setProjectForm((prev) => ({ ...prev, customer_id: id }));
                        setProjModalCustomerDropdownOpen(false);
                      }}
                      label={t('quotations.add.modals.addProject.customer', { defaultValue: 'عميل' })}
                    />
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.startDate', { defaultValue: 'تاريخ البداية' })}</label>
                  <input type="date" name="start_date" value={projectForm.start_date} onChange={handleProjectFormChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.endDate', { defaultValue: 'تاريخ النهاية' })}</label>
                  <input type="date" name="end_date" value={projectForm.end_date} onChange={handleProjectFormChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.status', { defaultValue: 'الحالة' })}</label>
                  <select name="status" value={projectForm.status} onChange={handleProjectFormChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    <option value="planned">{t('quotations.add.modals.addProject.statusOptions.planned', { defaultValue: 'مخطط' })}</option>
                    <option value="in_progress">{t('quotations.add.modals.addProject.statusOptions.inProgress', { defaultValue: 'قيد التنفيذ' })}</option>
                    <option value="paused">{t('quotations.add.modals.addProject.statusOptions.paused', { defaultValue: 'متوقف' })}</option>
                    <option value="completed">{t('quotations.add.modals.addProject.statusOptions.completed', { defaultValue: 'مكتمل' })}</option>
                    <option value="cancelled">{t('quotations.add.modals.addProject.statusOptions.cancelled', { defaultValue: 'ملغى' })}</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.contractValue', { defaultValue: 'قيمة العقد' })}</label>
                  <input type="number" name="contract_value" value={projectForm.contract_value} onChange={handleProjectFormChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                </div>

                <div style={{ position: 'relative' }} data-dropdown-container>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 'bold', color: 'white', flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addProject.tax', { defaultValue: 'الضريبة' })}
                    </label>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setShowAddTaxModal(true); }}
                      style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}
                    >
                      <FaPlus /> {t('common.add', { defaultValue: 'إضافة' })}
                    </button>
                  </div>
                  <div
                    data-dropdown-toggle
                    onClick={() => {
                      setProjModalCustomerDropdownOpen(false);
                      setCostCenterDropdownOpen(false);
                      if (taxDropdownOpen) { setTaxSearchTerm(''); setTaxCurrentPage(1); }
                      setTaxDropdownOpen((p) => !p);
                    }}
                    style={{ width: '100%', padding: '12px 15px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box' }}
                  >
                    <span>{projectForm.tax_id ? renderDropdownItemLabel((taxes || []).find(t => t.id === projectForm.tax_id) || {}, '', 'name_ar', 'name_en') : t('quotations.add.selectTax', { defaultValue: 'اختر الضريبة' })}</span>
                    <span style={{ fontSize: 12 }}>▼</span>
                  </div>
                  {taxDropdownOpen && (
                    <DropdownWithSearch
                      isOpen={taxDropdownOpen}
                      onToggle={setTaxDropdownOpen}
                      searchTerm={taxSearchTerm}
                      onSearchChange={setTaxSearchTerm}
                      searchInputRef={taxSearchInputRef}
                      items={taxes || []}
                      pagination={taxesPagination}
                      currentPage={taxCurrentPage}
                      onPageChange={setTaxCurrentPage}
                      onSelect={(id) => {
                        setProjectForm(prev => ({ ...prev, tax_id: id }));
                        setTaxDropdownOpen(false);
                      }}
                      label={t('quotations.add.modals.addProject.tax', { defaultValue: 'الضريبة' })}
                    />
                  )}
                </div>
                <div style={{ position: 'relative' }} data-dropdown-container>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 'bold', color: 'white', flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addProject.costCenter', { defaultValue: 'مركز التكلفة' })}
                    </label>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setShowAddCostCenterModal(true); }}
                      style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}
                    >
                      <FaPlus /> {t('common.add', { defaultValue: 'إضافة' })}
                    </button>
                  </div>
                  <div
                    data-dropdown-toggle
                    onClick={() => {
                      setProjModalCustomerDropdownOpen(false);
                      setTaxDropdownOpen(false);
                      if (costCenterDropdownOpen) { setCostCenterSearchTerm(''); setCostCenterCurrentPage(1); }
                      setCostCenterDropdownOpen((p) => !p);
                    }}
                    style={{ width: '100%', padding: '12px 15px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box' }}
                  >
                    <span>{projectForm.cost_center_id ? renderDropdownItemLabel((costCenters || []).find(c => c.id === projectForm.cost_center_id) || {}, 'code', 'name_ar', 'name_en') : t('quotations.add.selectCostCenter', { defaultValue: 'اختر مركز التكلفة' })}</span>
                    <span style={{ fontSize: 12 }}>▼</span>
                  </div>
                  {costCenterDropdownOpen && (
                    <DropdownWithSearch
                      isOpen={costCenterDropdownOpen}
                      onToggle={setCostCenterDropdownOpen}
                      searchTerm={costCenterSearchTerm}
                      onSearchChange={setCostCenterSearchTerm}
                      searchInputRef={costCenterSearchInputRef}
                      items={costCenters || []}
                      pagination={costCentersPagination}
                      currentPage={costCenterCurrentPage}
                      onPageChange={setCostCenterCurrentPage}
                      onSelect={(id) => {
                        setProjectForm(prev => ({ ...prev, cost_center_id: id }));
                        setCostCenterDropdownOpen(false);
                      }}
                      label={t('quotations.add.modals.addProject.costCenter', { defaultValue: 'مركز التكلفة' })}
                    />
                  )}
                </div>
              </div>
              <div style={{ marginTop: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                  {t('quotations.add.modals.addProject.notes', { defaultValue: 'ملاحظات' })}
                </label>
                <textarea
                  ref={projectNotesTextareaRef}
                  name="notes"
                  value={projectForm.notes}
                  onChange={handleProjectFormChange}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    resize: 'vertical',
                    direction: isRTL ? 'rtl' : 'ltr',
                    textAlign: isRTL ? 'right' : 'left'
                  }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px' }}>
                <button
                  type="button"
                  onClick={() => setShowAddProjectModal(false)}
                  style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {t('common.cancel', { defaultValue: 'إلغاء' })}
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingProject}
                  style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: isSubmittingProject ? 'not-allowed' : 'pointer', opacity: isSubmittingProject ? 0.7 : 1 }}
                >
                  {isSubmittingProject ? t('common.saving', { defaultValue: 'جاري الحفظ...' }) : t('common.save', { defaultValue: 'حفظ' })}
                </button>
              </div>
            </form>
          </div>
        </div>
        , document.body)}

      {/* Add Tax Modal */}
      {showAddTaxModal && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999,
            padding: '20px'
          }}
          onClick={() => setShowAddTaxModal(false)}
        >
          <div
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>
                {t('quotations.add.modals.addTax.title', { defaultValue: 'إضافة ضريبة جديدة' })}
              </h2>
              <button
                onClick={() => setShowAddTaxModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddTax}>
              <div style={{ display: 'grid', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addTax.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_ar"
                    value={taxForm.name_ar}
                    onChange={handleTaxFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: 'rtl',
                      textAlign: 'right'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addTax.nameEn', { defaultValue: 'الاسم بالإنجليزية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_en"
                    value={taxForm.name_en}
                    onChange={handleTaxFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: 'ltr',
                      textAlign: 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addTax.rate', { defaultValue: 'النسبة (%)' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="number"
                    name="rate"
                    value={taxForm.rate}
                    onChange={handleTaxFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={taxForm.is_active}
                    onChange={handleTaxFormChange}
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                  <label style={{ color: 'white', fontSize: '14px', cursor: 'pointer' }}>
                    {t('quotations.add.modals.addTax.isActive', { defaultValue: 'نشط' })}
                  </label>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px' }}>
                <button
                  type="button"
                  onClick={() => setShowAddTaxModal(false)}
                  style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {t('common.cancel', { defaultValue: 'إلغاء' })}
                </button>
                <button
                  type="submit"
                  style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {t('common.save', { defaultValue: 'حفظ' })}
                </button>
              </div>
            </form>
          </div>
        </div>
        , document.body)}


      {/* Add Cost Center Modal */}
      {showAddCostCenterModal && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999,
            padding: '20px'
          }}
          onClick={() => setShowAddCostCenterModal(false)}
        >
          <div
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>
                {t('quotations.add.modals.addCostCenter.title', { defaultValue: 'إضافة مركز تكلفة جديد' })}
              </h2>
              <button
                onClick={() => setShowAddCostCenterModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddCostCenter}>
              <div style={{ display: 'grid', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCostCenter.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_ar"
                    value={costCenterForm.name_ar}
                    onChange={handleCostCenterFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: 'rtl',
                      textAlign: 'right'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCostCenter.nameEn', { defaultValue: 'الاسم بالإنجليزية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_en"
                    value={costCenterForm.name_en}
                    onChange={handleCostCenterFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: 'ltr',
                      textAlign: 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCostCenter.code', { defaultValue: 'الكود' })}
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={costCenterForm.code}
                    onChange={handleCostCenterFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px' }}>
                <button
                  type="button"
                  onClick={() => setShowAddCostCenterModal(false)}
                  style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {t('common.cancel', { defaultValue: 'إلغاء' })}
                </button>
                <button
                  type="submit"
                  style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {t('common.save', { defaultValue: 'حفظ' })}
                </button>
              </div>
            </form>
          </div>
        </div>
        , document.body)}

    </div>
  );
};

export default AddNoteReceivable;


